"use client";

import React, { useState, useEffect, useRef } from "react";
import { ArrowLeftRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.1,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
function BeforeAfterSlider() {
  const [position, setPosition] = useState(50);
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <div ref={ref} className={`relative w-full aspect-[3/4] md:aspect-[4/5] rounded-3xl overflow-hidden shadow-xl transition-all duration-1000 ease-out transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      {}
      <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1668432/img/custom_patio_after.png" alt="Completed custom patio installation" className="absolute inset-0 w-full h-full object-cover" />

      {}
      <div className="absolute inset-0 w-full h-full" style={{
      clipPath: `inset(0 ${100 - position}% 0 0)`
    }}>
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1668432/img/custom_patio_before.png" alt="Unfinished backyard before patio" className="absolute inset-0 w-full h-full object-cover filter grayscale-[10%]" />
      </div>

      {}
      <div className="absolute top-0 bottom-0 w-1 bg-white pointer-events-none z-10" style={{
      left: `${position}%`
    }}>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow-xl text-primary border border-border/50 transition-transform hover:scale-110">
          <ArrowLeftRight size={20} strokeWidth={2.5} />
        </div>
      </div>

      {}
      <input type="range" min="0" max="100" value={position} onChange={e => setPosition(Number(e.target.value))} className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize z-20" aria-label="Drag to compare before and after landscaping" />

      {}
      <div className="absolute top-6 left-6 bg-background/90 backdrop-blur-md px-4 py-2 rounded-md text-sm font-semibold tracking-wide text-foreground z-10 pointer-events-none shadow-sm border border-border/50 transition-opacity duration-300">
        Before
      </div>
      <div className="absolute top-6 right-6 bg-primary text-primary-foreground px-4 py-2 rounded-md text-sm font-semibold tracking-wide z-10 pointer-events-none shadow-sm transition-opacity duration-300">
        After
      </div>
    </div>;
}
const galleryImages = [{
  src: "https://pro-kit.10web.cloud/wp-content/uploads/2026/05/2023-11-23-1.jpg?v=ew0suxea9j9",
  alt: "Modern cedar fencing installation",
  delay: "delay-100"
}, {
  src: "https://pro-kit.10web.cloud/wp-content/uploads/2026/05/2023-11-22-1.webp?v=007fl3gfgrue",
  alt: "Fresh sod installation for a lush lawn",
  delay: "delay-200"
}, {
  src: "https://pro-kit.10web.cloud/wp-content/uploads/2026/05/2023-11-22.webp?v=w1vqaaqvdzo",
  alt: "Professional landscape design plan",
  delay: "delay-300"
}, {
  src: "https://pro-kit.10web.cloud/wp-content/uploads/2026/05/2023-11-22-1-1.jpg?v=itoxbbayyej",
  alt: "Fresh bark mulch and flower bed maintenance",
  delay: "delay-400"
}];
export default function TheTransformationGallery() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="222474" id="our-signature-transformations" className="py-24 md:py-32 bg-background text-foreground overflow-hidden">
      <div className="container mx-auto px-6 md:px-8 max-w-7xl">
        
        {}
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-16 md:mb-24 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-accent text-accent-foreground text-sm font-medium mb-6">
            <Sparkles size={16} />
            <span>Organic Precision</span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold font-default text-primary tracking-tight mb-6">
            Our Signature Transformations
          </h2>
          <p className="text-lg md:text-xl text-slate-600 font-default leading-relaxed">
            Witness the evolution of outdoor living. From custom patio masonry to 
            precision fencing and lush sod installation, we craft landscapes that 
            blend natural beauty with architectural excellence.
          </p>
        </div>

        {}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-10 items-start">
          
          {}
          <div className="flex flex-col gap-4 md:gap-10 mt-8 lg:mt-16">
            {galleryImages.slice(0, 2).map((img, i) => <GalleryItem key={i} data-index={i} image={img} />)}
          </div>

          {}
          <div className="col-span-2 lg:col-span-2 order-first lg:order-none mb-8 lg:mb-0">
            <BeforeAfterSlider />
          </div>

          {}
          <div className="flex flex-col gap-4 md:gap-10 mt-8 lg:mt-32">
            {galleryImages.slice(2, 4).map((img, i) => <GalleryItem key={i + 2} data-index={i + 2} image={img} />)}
          </div>

        </div>

        {}
        <div className="mt-24 text-center">
          <Button size="lg" className="rounded-md px-8 h-14 text-base font-semibold shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 bg-primary text-primary-foreground">
            Book Your Free Consultation
          </Button>
        </div>

      </div>
    </section>;
}
function GalleryItem({
  image,
  "data-index": dataIndex
}: {
  image: any;
  "data-index": number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <div ref={ref} data-index={dataIndex} className={`relative rounded-2xl overflow-hidden shadow-md group transition-all duration-1000 ease-out transform ${image.delay} ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      <div className="aspect-[3/4] w-full bg-muted">
        <img src={image.src} alt={image.alt} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
      </div>
      {}
      <div className="absolute inset-0 bg-gradient-to-t from-primary/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
    </div>;
}